import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { auth } from './services/firebase';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import TimeTracking from './pages/TimeTracking';
import Clients from './pages/Clients';
import Invoices from './pages/Invoices';
import Payments from './pages/Payments';
import Subscriptions from './pages/Subscriptions';
import PricingOptimization from './pages/PricingOptimization';
import AIAgents from './pages/AIAgents';
import APIKeys from './pages/APIKeys';
import Settings from './pages/Settings';
import CreditNotes from './pages/CreditNotes';
import PartnerProjects from './pages/PartnerProjects';
import CostDiscovery from './pages/CostDiscovery';
import Accounts from './pages/Accounts';
import Documentation from './pages/Documentation';
import Reports from './pages/Reports';
import Expenses from './pages/Expenses';
import PortalEmails from './pages/PortalEmails';

// Partner Portal (public pages)
import PartnerPortal from './pages/PartnerPortal';
import PortalInvoiceDetail from './pages/PortalInvoiceDetail';
import PortalPaymentSetup from './pages/PortalPaymentSetup';
import PortalLogin from './pages/PortalLogin';
import PortalMagicVerify from './pages/PortalMagicVerify';

// Legal pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Components
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';

function App() {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    // Initialize Firebase Auth listener only if Firebase is configured
    // The initialize function handles both cases now
    const unsubscribe = initialize();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [initialize]);

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Partner Portal (public, token-based auth) */}
        <Route path="/portal/:accessToken" element={<PartnerPortal />} />
        <Route path="/portal/:accessToken/login" element={<PortalLogin />} />
        <Route path="/portal/:accessToken/invoice/:invoiceId" element={<PortalInvoiceDetail />} />
        <Route path="/portal/:accessToken/payment-setup" element={<PortalPaymentSetup />} />
        <Route path="/portal/auth/magic/:magicToken" element={<PortalMagicVerify />} />

        {/* Client shortcuts — tokens must match partnerAccess docs seeded by scripts/provision-portal-clients.js */}
        <Route path="/fitbreak" element={<Navigate to="/portal/43942b20df72be53461a88a580e98dfc50594cb557d848ed8f5686b2453de7a2" replace />} />
        <Route path="/portal/fitbreak" element={<Navigate to="/portal/43942b20df72be53461a88a580e98dfc50594cb557d848ed8f5686b2453de7a2" replace />} />
        <Route path="/portal/fitbreak/login" element={<Navigate to="/portal/43942b20df72be53461a88a580e98dfc50594cb557d848ed8f5686b2453de7a2/login" replace />} />
        <Route path="/jhorell" element={<Navigate to="/portal/3b5a3220610b4d231d37dacc5032f3a68686b4b73e780ef5c1c41e54661e7d3c" replace />} />
        <Route path="/portal/jhorell" element={<Navigate to="/portal/3b5a3220610b4d231d37dacc5032f3a68686b4b73e780ef5c1c41e54661e7d3c" replace />} />
        <Route path="/portal/jhorell/login" element={<Navigate to="/portal/3b5a3220610b4d231d37dacc5032f3a68686b4b73e780ef5c1c41e54661e7d3c/login" replace />} />

        <Route path="/masayagourmet" element={<Navigate to="/portal/932fbd8764253337f989e84b877e1b06d3f1388bb65bd5b3d072ba6cc2af8388" replace />} />
        <Route path="/portal/masayagourmet" element={<Navigate to="/portal/932fbd8764253337f989e84b877e1b06d3f1388bb65bd5b3d072ba6cc2af8388" replace />} />
        <Route path="/portal/masayagourmet/login" element={<Navigate to="/portal/932fbd8764253337f989e84b877e1b06d3f1388bb65bd5b3d072ba6cc2af8388/login" replace />} />

        <Route path="/casamanila" element={<Navigate to="/portal/f34a06c665d0920854fdc3e45a485468448a367b1eb95cfd80719e8cf3a321f4" replace />} />
        <Route path="/portal/casamanila" element={<Navigate to="/portal/f34a06c665d0920854fdc3e45a485468448a367b1eb95cfd80719e8cf3a321f4" replace />} />
        <Route path="/portal/casamanila/login" element={<Navigate to="/portal/f34a06c665d0920854fdc3e45a485468448a367b1eb95cfd80719e8cf3a321f4/login" replace />} />

        <Route path="/sabasbeach" element={<Navigate to="/portal/82cb1255b3ffe7d25a9cf3e8bba9392b086f912825daef31ab4d80867c039ce0" replace />} />
        <Route path="/portal/sabasbeach" element={<Navigate to="/portal/82cb1255b3ffe7d25a9cf3e8bba9392b086f912825daef31ab4d80867c039ce0" replace />} />
        <Route path="/portal/sabasbeach/login" element={<Navigate to="/portal/82cb1255b3ffe7d25a9cf3e8bba9392b086f912825daef31ab4d80867c039ce0/login" replace />} />

        <Route path="/fbd" element={<Navigate to="/portal/c2dca6f60b7b0329bbe40d8613f4b599a544e5149fe2caa4bcf5a18c391426a8" replace />} />
        <Route path="/portal/fbd" element={<Navigate to="/portal/c2dca6f60b7b0329bbe40d8613f4b599a544e5149fe2caa4bcf5a18c391426a8" replace />} />
        <Route path="/portal/fbd/login" element={<Navigate to="/portal/c2dca6f60b7b0329bbe40d8613f4b599a544e5149fe2caa4bcf5a18c391426a8/login" replace />} />

        <Route path="/omphbalugo" element={<Navigate to="/portal/571fa255624bd43a8bd2ec7380d781ffdd486c1e210ef82819f4753f29fae915" replace />} />
        <Route path="/portal/omphbalugo" element={<Navigate to="/portal/571fa255624bd43a8bd2ec7380d781ffdd486c1e210ef82819f4753f29fae915" replace />} />
        <Route path="/portal/omphbalugo/login" element={<Navigate to="/portal/571fa255624bd43a8bd2ec7380d781ffdd486c1e210ef82819f4753f29fae915/login" replace />} />

        <Route path="/jpa" element={<Navigate to="/portal/3e446e9d8ee77a99c57e0b678567ecbd6e5e55c69357724abdd507a945f56109" replace />} />
        <Route path="/portal/jpa" element={<Navigate to="/portal/3e446e9d8ee77a99c57e0b678567ecbd6e5e55c69357724abdd507a945f56109" replace />} />
        <Route path="/portal/jpa/login" element={<Navigate to="/portal/3e446e9d8ee77a99c57e0b678567ecbd6e5e55c69357724abdd507a945f56109/login" replace />} />

        <Route path="/aiskwela" element={<Navigate to="/portal/bf40aaf3d73d59679b06f701e6fa1795c1878307d423627f3431db905149f4a1" replace />} />
        <Route path="/portal/aiskwela" element={<Navigate to="/portal/bf40aaf3d73d59679b06f701e6fa1795c1878307d423627f3431db905149f4a1" replace />} />
        <Route path="/portal/aiskwela/login" element={<Navigate to="/portal/bf40aaf3d73d59679b06f701e6fa1795c1878307d423627f3431db905149f4a1/login" replace />} />

        <Route path="/pccc" element={<Navigate to="/portal/6e72011a25603d14f94373dcb7c33d446039f32a69033ffff8941b8ccb990297" replace />} />
        <Route path="/portal/pccc" element={<Navigate to="/portal/6e72011a25603d14f94373dcb7c33d446039f32a69033ffff8941b8ccb990297" replace />} />
        <Route path="/portal/pccc/login" element={<Navigate to="/portal/6e72011a25603d14f94373dcb7c33d446039f32a69033ffff8941b8ccb990297/login" replace />} />

        <Route path="/pcct" element={<Navigate to="/portal/c65695f3c5b10816e924042e25cc3f86db30c24e8ee6421051f9feb6c7a1aea9" replace />} />
        <Route path="/portal/pcct" element={<Navigate to="/portal/c65695f3c5b10816e924042e25cc3f86db30c24e8ee6421051f9feb6c7a1aea9" replace />} />
        <Route path="/portal/pcct/login" element={<Navigate to="/portal/c65695f3c5b10816e924042e25cc3f86db30c24e8ee6421051f9feb6c7a1aea9/login" replace />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="time" element={<TimeTracking />} />
          <Route path="clients" element={<Clients />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="payments" element={<Payments />} />
          <Route path="credit-notes" element={<CreditNotes />} />
          <Route path="subscriptions" element={<Subscriptions />} />
          <Route path="pricing" element={<PricingOptimization />} />
          <Route path="ai-agents" element={<AIAgents />} />
          <Route path="api-keys" element={<APIKeys />} />
          <Route path="partner-projects" element={<PartnerProjects />} />
          <Route path="cost-discovery" element={<CostDiscovery />} />
          <Route path="accounts" element={<Accounts />} />
          <Route path="expenses" element={<Expenses />} />
          <Route path="reports" element={<Reports />} />
          <Route path="portal-emails" element={<PortalEmails />} />
          <Route path="settings" element={<Settings />} />
          <Route path="docs" element={<Documentation />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
